/**
 * sound — app-wide one-liner facade over the engine AudioManager.
 *
 *   import { sound, SoundId } from '../engine/audio/sound';
 *   sound.play(SoundId.UI_TAP);
 *
 * This is the "easy to call from anywhere" layer (Phone Dice's SoundManager.play
 * equivalent), but typed and bus-aware. It owns nothing the AudioManager doesn't
 * already do — it just removes the need to thread an `engine.audio` reference
 * through every UI component, and adds per-play pitch jitter so repeated sounds
 * (dice clatter, cash, taps) don't sound canned.
 *
 * Silent-safe: before initSound() runs, or while SOUND_ASSETS_READY is false,
 * every method is a no-op. Triggers can be wired throughout the game today and
 * simply come alive when the files land and the flag flips.
 */

import type { AudioManager } from './AudioManager';
import { SoundId, SOUNDS, SOUND_ASSETS_READY, buildSoundConfigs } from './SoundCatalog';

export { SoundId } from './SoundCatalog';

export interface PlayOptions {
  /** Multiplier on the sound's catalog volume (0..1). */
  volume?: number;
  /** Explicit playback rate; overrides the catalog's `vary` jitter. */
  rate?: number;
  loop?: boolean;
}

class SoundService {
  private manager: AudioManager | null = null;
  private ready = false;

  /**
   * Wire the live AudioManager and preload the catalog. Call once during boot,
   * after the Engine exists. Preload is fire-and-forget and tolerant — a
   * missing/un-decodable file can never block or break startup.
   */
  init(manager: AudioManager): void {
    this.manager = manager;
    // When the global flag is off, register only the per-sound `ready` entries
    // (their files exist) so sounds can go live incrementally; flipping
    // SOUND_ASSETS_READY loads the whole catalog.
    const configs = buildSoundConfigs(!SOUND_ASSETS_READY);
    if (Object.keys(configs).length === 0) return; // nothing ready — stay silent
    this.ready = true;
    // Tolerant preload: swallow load errors so one bad file doesn't reject boot.
    manager.preload(configs).catch(() => { /* missing sfx — non-fatal */ });
  }

  /** True once the catalog is registered and assets are expected to exist. */
  isReady(): boolean {
    return this.ready && this.manager !== null;
  }

  /**
   * Globally suppress one-shot plays. Used by the throw pre-run: it steps the
   * LIVE physics world headlessly, so dice 'collide' listeners fire for
   * simulated impacts that must stay silent. Always pair with a finally —
   * a leaked suspend mutes the whole game.
   */
  suspend(on: boolean): void {
    this.suspended = on;
  }

  private suspended = false;

  /** Fire a one-shot (or looped) sound. No-op until ready. */
  play(id: SoundId, opts?: PlayOptions): number | null {
    if (this.suspended) return null;
    if (!this.ready || !this.manager) return null;

    // Always route through playWith with an explicit base volume so the
    // per-sound catalog volume AND the current bus volume both apply on every
    // play (AudioManager.play/setVolume only track the bus volume).
    const base = (opts?.volume ?? 1) * (SOUNDS[id]?.volume ?? 1);
    return this.manager.playWith(id, {
      volume: base,
      rate: opts?.rate ?? this.jitter(id),
      loop: opts?.loop,
    });
  }

  stop(id: SoundId, howlId?: number): void {
    this.manager?.stop(id, howlId);
  }

  /** Crossfade to a looping music track (catalog category must be 'music'). */
  playMusic(id: SoundId, fadeMs = 1000): void {
    if (!this.ready) return;
    this.manager?.playMusic(id, fadeMs);
  }

  stopMusic(fadeMs = 1000): void {
    this.manager?.stopMusic(fadeMs);
  }

  /** ± rate jitter from the catalog, so repeats vary. undefined = no jitter. */
  private jitter(id: SoundId): number | undefined {
    const vary = SOUNDS[id]?.vary;
    if (!vary) return undefined;
    return 1 + (Math.random() * 2 - 1) * vary;
  }
}

/** App-wide singleton. */
export const sound = new SoundService();
